import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import AdminShell from './AdminShell';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { InputField } from '../../ui/input-field';
import { SelectField } from '../../ui/select-field';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { REPORTING_YEARS } from '../fixtures';
import { Info, Save, ArrowLeft } from 'lucide-react';

const CreateCampaign = () => {
  const history = useHistory();
  const [name, setName] = useState('FY 2025-26 WFH & Commuting Survey');
  const [period, setPeriod] = useState(REPORTING_YEARS[1].value);
  const [deadline, setDeadline] = useState('2026-05-30');

  const submit = (e) => {
    e.preventDefault();
    history.push('/prototype/wfh-survey/admin/campaign/camp-draft?tab=employees');
  };

  return (
    <AdminShell
      breadcrumbs={[
        { label: 'Surveys', to: '/prototype/wfh-survey/admin/list' },
        { label: 'New campaign' },
      ]}
      eyebrow='Step 1 of 3'
      title='Create a new survey campaign'
      description='Name the campaign and pick the reporting period. You will add locations and invite employees in the next steps.'>
      <div className='grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8'>
        <Card>
          <CardHeader>
            <CardTitle>Campaign details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit}>
              <InputField
                label='Campaign name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                hint='Shown to employees in the invitation email. Use something they will recognise.'
                required
              />
              <SelectField
                label='Reporting period'
                value={period}
                onChange={setPeriod}
                options={REPORTING_YEARS}
                hint='Used to select DEFRA emission factors and pro-rate joiners.'
              />
              <InputField
                label='Deadline (optional)'
                type='date'
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                hint='We will auto-send reminder emails 7 and 2 days before this date.'
              />
              <div className='flex items-center justify-end gap-2 mt-6 pt-6 border-t border-border'>
                <Button variant='outline' type='button' asChild>
                  <Link to='/prototype/wfh-survey/admin/list'>
                    <ArrowLeft size={14} strokeWidth={1.5} />
                    Cancel
                  </Link>
                </Button>
                <Button type='submit'>
                  <Save size={14} strokeWidth={1.5} />
                  Save and continue
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className='space-y-4'>
          <Alert>
            <Info size={14} strokeWidth={1.5} />
            <AlertTitle>What happens next?</AlertTitle>
            <AlertDescription>
              After saving, you will be taken to the campaign page where you can add office locations, set FTE counts,
              and import an employee list.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className='text-sm'>Checklist</CardTitle>
            </CardHeader>
            <CardContent className='space-y-2 text-sm'>
              {[
                { done: true, label: 'Name the campaign' },
                { done: true, label: 'Pick reporting period' },
                { done: false, label: 'Add office locations & FTE' },
                { done: false, label: 'Import employee list' },
                { done: false, label: 'Send invitations' },
              ].map((item) => (
                <div key={item.label} className='flex items-center gap-2'>
                  <div
                    className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center ${
                      item.done ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'
                    }`}>
                    {item.done && <span className='text-[10px] leading-none'>&#10003;</span>}
                  </div>
                  <span className={item.done ? 'text-foreground' : 'text-muted-foreground'}>{item.label}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
};

export default CreateCampaign;
